// Pure logic extracted for the multi-file drop (S9/S10/S11 — HU-03) and multi-file selection
// (S23/S24 — HU-07) of documents attached to a TASK instruction. Kept free of React/API so it is
// directly unit-testable (same pattern as sun's DocumentAppendOrderCalculator, M3).

const MAX_FILE_NAME_LENGTH = 80

/**
 * RN03.2/RN07.2/RNT2: the default (editable) document name is the file name without its
 * extension. Only the LAST dot counts as the extension separator; a name with no dot, or a
 * dotfile whose dot is the first character, is returned unchanged.
 *
 * @param {string} fileName
 * @returns {string}
 */
const getDefaultDocumentName = (fileName = '') => {
  const lastDot = fileName.lastIndexOf('.')
  if (!hasFileExtension(fileName)) {
    return fileName
  }
  return fileName.substring(0, lastDot)
}

/**
 * @param {string} fileName
 * @returns {boolean} true when the file name has a non-empty extension after its last dot,
 * and that dot is not the first character (dotfiles like ".gitignore" have no extension).
 */
const hasFileExtension = (fileName = '') => {
  const lastDot = fileName.lastIndexOf('.')
  return lastDot > 0 && lastDot < fileName.length - 1
}

/**
 * D7: splits a batch of dropped/selected files into the ones that can be uploaded and the ones
 * that must be rejected, each with a reason. Only the offending file of the batch is rejected —
 * the rest of the batch is still accepted (S10/S24).
 *
 * @param {Array<{name: string, size: number}>} files
 * @param {number} [maxFileSize] max size in bytes; no limit applied when not provided
 * @returns {{accepted: Array, rejected: Array<{file: Object, reason: string}>}}
 */
const partitionUploadFiles = (files = [], maxFileSize) => {
  const accepted = []
  const rejected = []
  files.forEach((file) => {
    const fileName = file?.name || ''
    if (!hasFileExtension(fileName)) {
      rejected.push({ file, reason: 'NO_EXTENSION' })
      return
    }
    if (fileName.length > MAX_FILE_NAME_LENGTH) {
      rejected.push({ file, reason: 'FILE_NAME_TOO_LONG' })
      return
    }
    if (maxFileSize && file?.size > maxFileSize) {
      rejected.push({ file, reason: 'FILE_TOO_LARGE' })
      return
    }
    accepted.push(file)
  })
  return { accepted, rejected }
}

/**
 * One create payload per file of a dropped/selected batch, appended after the documents already
 * there: default name = file name without extension, default type, and CONSECUTIVE orders starting
 * at `startOrder`.
 *
 * The explicit orders are needed for temp documents (a workflow being edited): Sun recomputes the
 * append order server-side only when it persists the document (`document/create`), while
 * `document/createTemp` stores the order the client sends verbatim — a whole batch sent with the
 * same order would land as several documents sharing it.
 *
 * @param {Array<{name: string}>} files Files to upload, in the order they were dropped/selected
 * @param {number} startOrder Order for the first file of the batch
 * @param {Object} documentType Document type applied to the whole batch
 * @returns {Array<{file: Object, body: {documentName: string, description: string, documentType: Object, order: number}}>}
 */
const buildAppendDocumentPayloads = (files = [], startOrder = 1, documentType = {}) => {
  return files.map((file, index) => ({
    file,
    body: {
      documentName: getDefaultDocumentName(file?.name || ''),
      description: '',
      documentType,
      order: startOrder + index
    }
  }))
}

export { getDefaultDocumentName, hasFileExtension, partitionUploadFiles, buildAppendDocumentPayloads }
